import axios from "axios";
import toast from "react-hot-toast";
import { clearThumbnailCache } from "../components/ui/ThumbnailCache";
import { useAuthStore } from "../store/auth.store";
import { getDeviceFingerprint } from "../utils/fingerprint";

/**
 * API Client with Hybrid Token Authentication
 *
 * Security Architecture:
 * - Access Token: Stored in memory (Zustand store), sent in Authorization header
 * - Refresh Token: httpOnly cookie (set by backend), sent automatically with withCredentials
 *
 * Flow:
 * 1. Request interceptor adds access token from memory to Authorization header
 * 2. If 401 response, response interceptor attempts token refresh
 * 3. Refresh endpoint reads httpOnly cookie automatically (withCredentials: true)
 * 4. New access token stored in memory, request retried
 *
 * Logout Handling:
 * - All pending requests are aborted when logout is initiated
 * - Cross-tab logout sync via BroadcastChannel
 * - Requests are blocked during logout process
 */

/**
 * Extract error message from API error response
 * Handles cases where error might be an object with {code, message, stack}
 */
export const getErrorMessage = (error, fallback = "An error occurred") => {
  const errorData = error?.response?.data?.error;
  if (typeof errorData === "string") {
    return errorData;
  }
  if (errorData?.message) {
    return errorData.message;
  }
  if (error?.message) {
    return error.message;
  }
  return fallback;
};

/**
 * Parse JWT token and extract payload (without verification)
 */
const parseJwt = (token) => {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

/**
 * Check if token is expired or will expire within threshold
 */
const isTokenExpired = (token, thresholdSeconds = 0) => {
  const payload = parseJwt(token);
  if (!payload?.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp - now <= thresholdSeconds;
};

const TOKEN_REFRESH_THRESHOLD_SECONDS = 30; // Refresh 30s before expiry

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api/v1",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // CRITICAL: Send httpOnly cookies with every request
});

// Client for public token-authenticated endpoints (signing, verification,
// in-person sessions). Must resolve the same API origin as the main client;
// a hardcoded relative path only reaches the backend when the frontend host
// proxies /api, which production (CloudFront + separate API origin) does not.
// Kept separate from `api` on purpose: no Authorization header, and no
// 401-refresh interceptor; a 401 here means an invalid access code or
// signing token, not a stale session to refresh.
const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api/v1",
  headers: {
    "Content-Type": "application/json",
  },
});

// Signing endpoints record the signer's device fingerprint and timezone on
// the signature record, audit trail, and certificate of completion (the
// backend reads the X-Device-Fingerprint and X-Timezone headers in
// getClientInfo; the timezone lets the certificate show the signer's local
// signing time). Attach them to every public request; best-effort — never
// block a signing request on them.
publicApi.interceptors.request.use(
  async (config) => {
    try {
      config.headers["X-Timezone"] =
        Intl.DateTimeFormat().resolvedOptions().timeZone;
      config.headers["X-Device-Fingerprint"] = await getDeviceFingerprint();
    } catch {
      // Unavailable (restricted browser context); proceed without.
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ============================================================================
// LOGOUT STATE MANAGEMENT
// ============================================================================
let isLoggingOut = false;
let logoutAbortController = null;

/**
 * Check if logout is in progress
 */
export const isLogoutInProgress = () => isLoggingOut;

/**
 * Get abort signal for requests (to cancel on logout)
 */
const getAbortSignal = () => {
  if (!logoutAbortController) {
    logoutAbortController = new AbortController();
  }
  return logoutAbortController.signal;
};

/**
 * Abort all pending requests (called during logout)
 */
const abortAllRequests = () => {
  if (logoutAbortController) {
    logoutAbortController.abort();
    logoutAbortController = null;
  }
};

/**
 * Reset abort controller (called after logout complete)
 */
const resetAbortController = () => {
  logoutAbortController = new AbortController();
};

// ============================================================================
// CROSS-TAB LOGOUT SYNC (BroadcastChannel)
// ============================================================================
let authChannel = null;

/**
 * Initialize cross-tab auth synchronization (logout + token refresh)
 */
const initAuthChannel = () => {
  if (typeof BroadcastChannel === "undefined") {
    // Fallback for browsers without BroadcastChannel (Safari < 15.4)
    // Use storage events instead
    window.addEventListener("storage", (event) => {
      if (event.key === "escribe_logout" && event.newValue) {
        handleCrossTabLogout();
      }
      if (event.key === "escribe_token_refresh" && event.newValue) {
        try {
          const data = JSON.parse(event.newValue);
          handleCrossTabTokenRefresh(data);
        } catch {
          // Invalid JSON, ignore
        }
      }
    });
    return;
  }

  try {
    authChannel = new BroadcastChannel("escribe_auth");
    authChannel.onmessage = (event) => {
      if (event.data?.type === "logout") {
        handleCrossTabLogout();
      }
      if (event.data?.type === "token_refresh") {
        handleCrossTabTokenRefresh(event.data);
      }
    };
  } catch (e) {
    console.warn("BroadcastChannel not available:", e);
  }
};

/**
 * Broadcast logout to other tabs
 */
const broadcastLogout = () => {
  if (authChannel) {
    try {
      authChannel.postMessage({ type: "logout", timestamp: Date.now() });
    } catch (e) {
      console.warn("Failed to broadcast logout:", e);
    }
  }

  // Also use localStorage for browsers without BroadcastChannel
  try {
    localStorage.setItem("escribe_logout", Date.now().toString());
    // Remove immediately - we just need the storage event to fire
    setTimeout(() => localStorage.removeItem("escribe_logout"), 100);
  } catch {
    // localStorage might not be available
  }
};

/**
 * Broadcast token refresh to other tabs
 */
const broadcastTokenRefresh = (accessToken, user) => {
  const data = {
    type: "token_refresh",
    accessToken,
    user,
    timestamp: Date.now(),
  };

  if (authChannel) {
    try {
      authChannel.postMessage(data);
    } catch (e) {
      console.warn("Failed to broadcast token refresh:", e);
    }
  }

  // Also use localStorage for browsers without BroadcastChannel
  try {
    localStorage.setItem("escribe_token_refresh", JSON.stringify(data));
    setTimeout(() => localStorage.removeItem("escribe_token_refresh"), 100);
  } catch {
    // localStorage might not be available
  }
};

/**
 * Handle logout triggered from another tab
 */
const handleCrossTabLogout = () => {
  if (isLoggingOut) {
    return; // Already logging out
  }

  // Set logout state
  isLoggingOut = true;

  // Abort pending requests
  abortAllRequests();

  // Clear local state
  useAuthStore.getState().logout();

  // Clear thumbnail cache
  clearThumbnailCache();

  // Redirect to login (without calling API since other tab already did)
  if (!window.location.pathname.includes("/login")) {
    window.location.href = "/login?session=ended";
  }

  // Reset state
  isLoggingOut = false;
  resetAbortController();
};

/**
 * Handle token refresh from another tab
 * Update local access token to stay in sync
 */
const handleCrossTabTokenRefresh = (data) => {
  if (!data?.accessToken) {
    return;
  }

  // Update local access token
  useAuthStore.getState().setAccessToken(data.accessToken);
  if (data.user) {
    useAuthStore.getState().setUser(data.user);
  }

  // Clear any pending refresh promise since we got a fresh token
  refreshPromise = null;
};

// Initialize cross-tab sync on module load
initAuthChannel();

// ============================================================================
// CROSS-TAB REFRESH LOCK
// ============================================================================
const REFRESH_LOCK_KEY = "escribe_refresh_lock";
const REFRESH_LOCK_TIMEOUT = 10000; // 10 seconds max lock time

// Clean up any stale refresh locks on page load
try {
  const existingLock = localStorage.getItem(REFRESH_LOCK_KEY);
  if (existingLock) {
    const lockTime = parseInt(existingLock, 10);
    if (Date.now() - lockTime > REFRESH_LOCK_TIMEOUT) {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    }
  }
} catch {
  // localStorage not available
}

/**
 * Try to acquire cross-tab refresh lock
 * Uses localStorage with timestamp to prevent stale locks
 */
const tryAcquireRefreshLock = () => {
  try {
    const existingLock = localStorage.getItem(REFRESH_LOCK_KEY);
    if (existingLock) {
      const lockTime = parseInt(existingLock, 10);
      // If lock is older than timeout, it's stale - we can take it
      if (Date.now() - lockTime < REFRESH_LOCK_TIMEOUT) {
        return false; // Lock is still valid, another tab is refreshing
      }
    }
    // Acquire lock
    localStorage.setItem(REFRESH_LOCK_KEY, Date.now().toString());
    return true;
  } catch {
    // localStorage not available, proceed without lock
    return true;
  }
};

/**
 * Release cross-tab refresh lock
 */
const releaseRefreshLock = () => {
  try {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  } catch {
    // localStorage not available
  }
};

/**
 * Wait for another tab to complete refresh
 * Polls for lock release or listens for broadcast
 */
const waitForCrossTabRefresh = () =>
  new Promise((resolve) => {
    const maxWait = REFRESH_LOCK_TIMEOUT;
    const startTime = Date.now();
    const pollInterval = 100;

    const checkLock = () => {
      // Check if we got a new token from broadcast
      const currentToken = useAuthStore.getState().accessToken;
      if (
        currentToken &&
        !isTokenExpired(currentToken, TOKEN_REFRESH_THRESHOLD_SECONDS)
      ) {
        resolve({
          accessToken: currentToken,
          user: useAuthStore.getState().user,
        });
        return;
      }

      // Check if lock is released
      try {
        const lock = localStorage.getItem(REFRESH_LOCK_KEY);
        if (!lock) {
          // Lock released, check for token
          const token = useAuthStore.getState().accessToken;
          resolve({ accessToken: token, user: useAuthStore.getState().user });
          return;
        }
      } catch {
        // localStorage not available
      }

      // Timeout - proceed with our own refresh attempt
      if (Date.now() - startTime > maxWait) {
        resolve(null); // Signal to attempt refresh
        return;
      }

      // Keep polling
      setTimeout(checkLock, pollInterval);
    };

    checkLock();
  });

// ============================================================================
// TOKEN REFRESH STATE
// ============================================================================
// Single source of truth for refresh state - prevents race conditions
// All concurrent callers share the same promise
let refreshPromise = null;

/**
 * Centralized refresh function - ensures only one refresh happens at a time
 * All concurrent callers await the same promise
 *
 * Cross-Tab Coordination:
 * - Uses localStorage lock to prevent multiple tabs from refreshing simultaneously
 * - Broadcasts new token to other tabs via BroadcastChannel/localStorage
 * - If another tab is refreshing, waits for their result
 *
 * Returns: Promise<{ accessToken, user }> on success
 * Throws: Error on failure
 */
const doRefresh = () => {
  // Don't refresh if logging out
  if (isLoggingOut) {
    return Promise.reject(new Error("Logout in progress"));
  }

  // Don't refresh on auth/public pages
  const skipRefreshPages = [
    "/login",
    "/register",
    "/registration-success",
    "/forgot-password",
    "/reset-password",
    "/verify-email",
    "/sign/",
    "/signing-link/",
  ];
  const currentPath = window.location.pathname;
  if (
    skipRefreshPages.some(
      (page) => currentPath.startsWith(page) || currentPath.includes(page),
    )
  ) {
    return Promise.reject(new Error("Refresh skipped on auth page"));
  }

  // If already refreshing in this tab, return the existing promise
  // All callers will await the same promise and get the same result
  if (refreshPromise) {
    return refreshPromise;
  }

  // Start a new refresh - create the promise synchronously before any await
  refreshPromise = (async () => {
    try {
      // Try to acquire cross-tab lock
      const gotLock = tryAcquireRefreshLock();

      if (!gotLock) {
        // Another tab is refreshing, wait for them
        const result = await waitForCrossTabRefresh();

        if (
          result?.accessToken &&
          !isTokenExpired(result.accessToken, TOKEN_REFRESH_THRESHOLD_SECONDS)
        ) {
          return result;
        }

        // Other tab's refresh didn't give us a valid token, try to get lock again
        if (!tryAcquireRefreshLock()) {
          // Still can't get lock, return what we have or fail
          const currentToken = useAuthStore.getState().accessToken;
          if (currentToken && !isTokenExpired(currentToken, 0)) {
            return {
              accessToken: currentToken,
              user: useAuthStore.getState().user,
            };
          }
          throw new Error("Could not acquire refresh lock");
        }
      }

      // We have the lock, proceed with refresh
      try {
        const deviceFingerprint = await getDeviceFingerprint();
        // Use the same baseURL as the api instance to ensure correct domain in production
        const apiBaseUrl = import.meta.env.VITE_API_URL || "/api/v1";
        const response = await axios.post(
          `${apiBaseUrl}/auth/refresh`,
          { deviceFingerprint },
          { withCredentials: true },
        );

        const { accessToken, user } = response.data.data;

        if (accessToken) {
          useAuthStore.getState().setAccessToken(accessToken);
          if (user) {
            useAuthStore.getState().setUser(user);
          }

          // Broadcast to other tabs
          broadcastTokenRefresh(accessToken, user);
        }

        return { accessToken, user };
      } finally {
        // Always release the lock
        releaseRefreshLock();
      }
    } finally {
      // Clear the promise AFTER it completes (success or error)
      // This is safe because:
      // 1. The promise is already settled, so any new awaiter gets the result immediately
      // 2. We clear it to allow future refreshes
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Ensure we have a valid token before making a request
 * Multiple concurrent calls will wait for a single refresh operation
 */
const ensureValidToken = async () => {
  // Don't bother if logging out
  if (isLoggingOut) {
    return null;
  }

  const token = useAuthStore.getState().accessToken;

  if (!token) return null;

  // Check if token needs refresh
  if (isTokenExpired(token, TOKEN_REFRESH_THRESHOLD_SECONDS)) {
    try {
      // doRefresh handles deduplication - all callers share one promise
      const result = await doRefresh();
      return result?.accessToken || null;
    } catch {
      return null;
    }
  }

  return token;
};

// ============================================================================
// AUTH ENDPOINT DETECTION
// ============================================================================
const isAuthEndpoint = (url) => {
  const authPaths = [
    "/auth/login",
    "/auth/register",
    "/auth/refresh",
    "/auth/forgot-password",
    "/auth/reset-password",
    "/auth/verify-email",
    "/auth/verify-login-code",
    "/auth/resend-login-code",
    "/auth/resend-verification",
  ];
  return authPaths.some((path) => url?.includes(path));
};

const isPublicEndpoint = (url) => {
  const publicPaths = [
    "/sign/", // Public signing endpoints
    "/public/", // Explicit public endpoints
    "/health", // Health check
    "/signing-links/public/", // Public signing links
    "/kb/", // Knowledge base public endpoints (but not /kb/admin/)
  ];
  // Check if it's a KB admin endpoint (not public)
  if (url?.includes("/kb/admin/")) {
    return false;
  }
  return publicPaths.some((path) => url?.includes(path));
};

// ============================================================================
// REQUEST INTERCEPTOR - Proactive Token Refresh
// ============================================================================
api.interceptors.request.use(
  async (config) => {
    // Block requests if logging out (except logout request itself)
    if (isLoggingOut && !config.url?.includes("/auth/logout")) {
      const error = new Error("Request blocked: logout in progress");
      error.code = "LOGOUT_IN_PROGRESS";
      return Promise.reject(error);
    }

    // Add abort signal to all requests (for cancellation on logout)
    if (!config.signal) {
      config.signal = getAbortSignal();
    }

    // Skip token handling for auth/public endpoints
    if (isAuthEndpoint(config.url) || isPublicEndpoint(config.url)) {
      return config;
    }

    // Ensure we have a valid token before making the request
    const token = await ensureValidToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // If sending FormData, let axios set the Content-Type with boundary
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ============================================================================
// RESPONSE INTERCEPTOR - Handle 401 as Fallback
// ============================================================================
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If request was aborted due to logout, don't process further
    if (error.code === "ERR_CANCELED" || error.code === "LOGOUT_IN_PROGRESS") {
      return Promise.reject(error);
    }

    // Don't retry if logging out
    if (isLoggingOut) {
      return Promise.reject(error);
    }

    // Handle 401 errors - fallback in case proactive refresh missed something
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for auth endpoints
      if (
        isAuthEndpoint(originalRequest.url) ||
        isPublicEndpoint(originalRequest.url)
      ) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        // Use the same doRefresh mechanism - handles queueing automatically
        const result = await doRefresh();

        if (result?.accessToken) {
          originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
          return api(originalRequest);
        }
        throw new Error("No access token returned");
      } catch (refreshError) {
        // Only logout if currently authenticated and not already logging out
        const { isAuthenticated } = useAuthStore.getState();
        if (isAuthenticated && !isLoggingOut) {
          useAuthStore.getState().logout();
        }

        // Redirect to login if not already there
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login?session=expired";
        }
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    const message =
      error.response?.data?.error?.message || "An unexpected error occurred";

    // Don't show toast for 401s (handled above), 403s (permission), or cancelled requests
    if (
      error.response?.status !== 401 &&
      error.response?.status !== 403 &&
      !axios.isCancel(error) &&
      error.code !== "ERR_CANCELED"
    ) {
      // Only show toast for server errors, not validation errors
      if (error.response?.status >= 500) {
        toast.error(message);
      }
    }

    return Promise.reject(error);
  },
);

// ============================================================================
// LOGOUT FUNCTION - Centralized logout with cross-tab sync
// ============================================================================
/**
 * Perform logout - aborts requests, clears state, broadcasts to other tabs
 * This is the main logout function that should be called by useLogout hook
 */
export const performLogout = async () => {
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  // Abort all pending requests
  abortAllRequests();

  // Broadcast logout to other tabs BEFORE clearing state
  broadcastLogout();

  try {
    // Call backend to clear cookie and revoke token
    // Use a fresh axios instance to avoid interceptors blocking
    const apiBaseUrl = import.meta.env.VITE_API_URL || "/api/v1";
    await axios.post(
      `${apiBaseUrl}/auth/logout`,
      {},
      { withCredentials: true },
    );
  } catch (error) {
    console.warn("[Auth] Logout API error (continuing anyway):", error.message);
  }

  // Clear local state
  useAuthStore.getState().logout();

  // Clear thumbnail cache
  clearThumbnailCache();

  // Reset state for potential re-login
  isLoggingOut = false;
  resetAbortController();
};

/**
 * Initialize authentication on app start
 * Attempts to restore session from refresh token cookie
 *
 * Called by AuthProvider on mount
 */
export const initializeAuth = async () => {
  const {
    setLoading,
    restoreSession,
    initComplete,
    accessToken,
    user,
    isAuthenticated,
  } = useAuthStore.getState();

  // Skip refresh attempt on auth pages and public pages - no session expected/needed
  const skipRefreshPages = [
    "/login",
    "/register",
    "/forgot-password",
    "/reset-password",
    "/verify-email",
    "/sign/", // Public signing pages
    "/signing-link/", // Public signing links
    "/embed/", // Embedded flows (token-authenticated; no user session).
    // Never call /auth/refresh here; the embed runs in a third-party iframe.
    "/terms", // Public legal pages; anonymous by design
    "/privacy",
    "/electronic-signature-disclosure",
    "/subprocessors",
  ];
  const currentPath = window.location.pathname;
  const shouldSkipRefresh = skipRefreshPages.some(
    (page) => currentPath.startsWith(page) || currentPath.includes(page),
  );

  if (shouldSkipRefresh) {
    // On auth/public pages, just mark init complete without trying to refresh
    initComplete();
    return false;
  }

  // If we already have a valid, non-expired session, don't refresh
  if (
    accessToken &&
    user &&
    isAuthenticated &&
    !isTokenExpired(accessToken, TOKEN_REFRESH_THRESHOLD_SECONDS)
  ) {
    setLoading(false);
    return true;
  }

  setLoading(true);

  try {
    // doRefresh handles deduplication - safe to call even if refresh is in progress
    const result = await doRefresh();

    if (result?.accessToken && result?.user) {
      restoreSession(result.user, result.accessToken);
      return true;
    }

    initComplete();
    return false;
  } catch {
    // This is expected if user is not logged in - don't log as error
    initComplete();
    return false;
  }
};

/**
 * Get current token status (for debugging/UI)
 */
export const getTokenStatus = () => {
  const token = useAuthStore.getState().accessToken;
  if (!token) {
    return { valid: false, expiresIn: 0, needsRefresh: true };
  }

  const payload = parseJwt(token);
  const now = Math.floor(Date.now() / 1000);
  const expiresIn = payload?.exp ? Math.max(0, payload.exp - now) : 0;

  return {
    valid: !isTokenExpired(token, 0),
    expiresIn,
    needsRefresh: isTokenExpired(token, TOKEN_REFRESH_THRESHOLD_SECONDS),
  };
};

/**
 * Force a token refresh (for manual refresh button)
 */
export const forceTokenRefresh = async () => {
  try {
    // doRefresh handles deduplication - safe to call even if refresh is in progress
    const result = await doRefresh();
    const payload = parseJwt(result.accessToken);
    const now = Math.floor(Date.now() / 1000);
    return {
      success: true,
      expiresIn: payload?.exp ? payload.exp - now : 0,
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// AUTH API
// ============================================================================
export const authApi = {
  register: (data, securityHeaders = {}) =>
    api.post("/auth/register", data, { headers: securityHeaders }),
  login: async (data, securityHeaders = {}) => {
    const deviceFingerprint = await getDeviceFingerprint();
    return api.post(
      "/auth/login",
      { ...data, deviceFingerprint },
      { headers: securityHeaders },
    );
  },
  // Email 2FA verification during login
  verifyLoginCode: (data) => api.post("/auth/verify-login-code", data),
  resendLoginCode: (data) => api.post("/auth/resend-login-code", data),
  // Logout - clears httpOnly cookie on backend
  logout: () => api.post("/auth/logout"),
  logoutAll: () => api.post("/auth/logout-all"),
  deleteAccount: () => api.delete("/auth/account"),
  // Subject-access export (GDPR Art. 15/20): all personal data as JSON
  exportMyData: () => api.get("/auth/me/export", { responseType: "blob" }),
  // Refresh - cookie sent automatically, no token in body
  refreshToken: async () => {
    const deviceFingerprint = await getDeviceFingerprint();
    return api.post("/auth/refresh", { deviceFingerprint });
  },
  getProfile: () => api.get("/auth/profile"),
  updateProfile: (data) => api.patch("/auth/profile", data),
  // Avatar upload/delete
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append("avatar", file);
    return api.post("/auth/profile/avatar", formData, {
      headers: { "Content-Type": undefined },
    });
  },
  deleteAvatar: () => api.delete("/auth/profile/avatar"),
  changePassword: (currentPassword, newPassword) =>
    api.post("/auth/change-password", { currentPassword, newPassword }),
  forgotPassword: (email, securityHeaders = {}) =>
    api.post("/auth/forgot-password", { email }, { headers: securityHeaders }),
  resetPassword: (token, password, securityHeaders = {}) =>
    api.post(
      "/auth/reset-password",
      { token, password },
      { headers: securityHeaders },
    ),
  verifyEmail: (token) => api.post("/auth/verify-email", { token }),
  resendVerification: (data, securityHeaders = {}) =>
    api.post("/auth/resend-verification", data, { headers: securityHeaders }),
  // 2FA management (switch between email and TOTP)
  setup2FA: () => api.post("/auth/2fa/setup"),
  verify2FA: (code) => api.post("/auth/2fa/verify", { code }),
  disable2FA: (switchToEmail = true) =>
    api.post("/auth/2fa/disable", { switchToEmail }),
  // Onboarding
  completeOnboarding: () => api.post("/auth/onboarding/complete"),
  resetOnboarding: () => api.post("/auth/onboarding/reset"),
  getOnboardingStatus: () => api.get("/auth/onboarding"),
  // WebAuthn 2FA login options (after password entry)
  getWebAuthnLoginOptions: (userId) =>
    api.post("/auth/webauthn-login-options", { userId }),
  // Verify WebAuthn 2FA login
  verifyWebAuthnLogin: ({ userId, credential }) =>
    api.post("/auth/verify-webauthn-login", { userId, credential }),
  // Set 2FA method (email, totp, or webauthn)
  setTwoFactorMethod: (method) => api.put("/auth/2fa/method", { method }),
  // Passwordless passkey login - get options (no email/password needed)
  getPasswordlessAuthOptions: () => api.post("/auth/passkey/options"),
  // Passwordless passkey login - verify credential
  verifyPasswordlessLogin: ({ sessionId, credential }) =>
    api.post("/auth/passkey/verify", { sessionId, credential }),
};

// ============================================================================
// SSO API
// ============================================================================
export const ssoApi = {
  checkSSO: (email) => api.post("/sso/check", { email }),
  getProviders: () => api.get("/sso/providers"),
  initiateLogin: (provider) => api.post("/sso/login", { provider }),
  callback: (provider, code, state) =>
    api.post("/sso/callback", { provider, code, state }),
  link: (provider) => api.post("/sso/link", { provider }),
  unlink: (provider) => api.delete(`/sso/link/${provider}`),
  // SSO Configuration management (for org admins)
  getConfigurations: () => api.get("/sso/configurations"),
  getConfiguration: (id) => api.get(`/sso/configurations/${id}`),
  saveConfiguration: (data) => api.post("/sso/configurations", data),
  deleteConfiguration: (id) => api.delete(`/sso/configurations/${id}`),
  testConfiguration: (id) => api.post(`/sso/configurations/${id}/test`),
  // Domain management
  getDomains: () => api.get("/sso/domains"),
  addDomain: (configId, domain) =>
    api.post("/sso/domains", { configId, domain }),
  verifyDomain: (domain) => api.post(`/sso/domains/${domain}/verify`),
  deleteDomain: (domain) => api.delete(`/sso/domains/${domain}`),
  // Login attempts
  getLoginAttempts: (limit = 20) =>
    api.get("/sso/attempts", { params: { limit } }),
};

// ============================================================================
// AI API
// ============================================================================
export const aiApi = {
  analyzeDocument: (documentId) =>
    api.post("/ai/analyze-document", { documentId }),
  suggestFields: (envelopeId) => api.post("/ai/suggest-fields", { envelopeId }),
  extractText: (documentId) => api.post("/ai/extract-text", { documentId }),
  summarize: (documentId) => api.post("/ai/summarize", { documentId }),
  chat: (message, context) => api.post("/ai/chat", { message, context }),
  generateTemplate: (description) =>
    api.post("/ai/generate-template", { description }),
  // ADD THIS:
  detectFields: (envelopeId, documentId) =>
    api.post(`/ai/detect-fields`, { envelopeId, documentId }),
};

// ============================================================================
// FEATURES / PLAN API (for regular users to check their features)
// ============================================================================
export const featuresApi = {
  getFeatures: () => api.get("/features"),
  checkFeature: (featureKey) => api.get(`/features/${featureKey}`),
  getPlanLimits: () => api.get("/features/limits"),
  getUsage: () => api.get("/features/usage"),
  getPlan: () => api.get("/features/plan"),
};

// ============================================================================
// ADMIN API (App Owner Only - Consolidated)
// ============================================================================
export const adminApi = {
  // Dashboard & Stats
  getDashboard: () => api.get("/admin/dashboard"),
  getStats: () => api.get("/admin/stats"),

  // Users Management
  getUsers: (params) => api.get("/admin/users", { params }),
  getUserById: (id) => api.get(`/admin/users/${id}`),
  createUser: (data) => api.post("/admin/users", data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  toggleUserFeature: (id, feature, enabled) =>
    api.post(`/admin/users/${id}/features`, { feature, enabled }),
  enableAllUserFeatures: (id) =>
    api.post(`/admin/users/${id}/features/enable-all`),
  suspendUser: (id) => api.post(`/admin/users/${id}/suspend`),
  unsuspendUser: (id) => api.post(`/admin/users/${id}/unsuspend`),
  impersonateUser: (id) => api.post(`/admin/users/${id}/impersonate`),
  resetUserPassword: (id) => api.post(`/admin/users/${id}/reset-password`),
  getUserActivity: (id, params) =>
    api.get(`/admin/users/${id}/activity`, { params }),

  // Organisations Management
  getOrganizations: (params) => api.get("/admin/organisations", { params }),
  getOrganizationById: (id) => api.get(`/admin/organisations/${id}`),
  createOrganization: (data) => api.post("/admin/organisations", data),
  updateOrganization: (id, data) => api.put(`/admin/organisations/${id}`, data),
  deleteOrganization: (id) => api.delete(`/admin/organisations/${id}`),
  getOrganizationMembers: (id) => api.get(`/admin/organisations/${id}/members`),
  getOrganizationUsage: (id) => api.get(`/admin/organisations/${id}/usage`),
  getOrganizationBilling: (id) => api.get(`/admin/organisations/${id}/billing`),
  updateOrganizationPlan: (id, data) =>
    api.put(`/admin/organisations/${id}/plan`, data),

  // Feature Overrides (for organisations)
  getOrganizationFeatures: (orgId) =>
    api.get(`/admin/organisations/${orgId}/features`),
  setFeatureOverride: (orgId, data) =>
    api.post(`/admin/organisations/${orgId}/features`, data),
  removeFeatureOverride: (orgId, feature) =>
    api.delete(`/admin/organisations/${orgId}/features/${feature}`),

  // Feature Flags (global)
  getFeatureFlags: () => api.get("/admin/features"),
  getFeatureFlagById: (id) => api.get(`/admin/features/${id}`),
  createFeatureFlag: (data) => api.post("/admin/features", data),
  updateFeatureFlag: (id, data) => api.patch(`/admin/features/${id}`, data),
  deleteFeatureFlag: (id) => api.delete(`/admin/features/${id}`),
  toggleFeatureFlag: (id) => api.post(`/admin/features/${id}/toggle`),

  // Plans Management
  getPlans: () => api.get("/admin/plans"),
  createPlan: (data) => api.post("/admin/plans", data),
  updatePlan: (id, data) => api.patch(`/admin/plans/${id}`, data),
  deletePlan: (id) => api.delete(`/admin/plans/${id}`),

  // Billing Management
  getBillingOverview: () => api.get("/admin/billing/overview"),
  getTransactions: (params) =>
    api.get("/admin/billing/transactions", { params }),
  getSubscriptions: (params) =>
    api.get("/admin/billing/subscriptions", { params }),
  refundTransaction: (transactionId, amount) =>
    api.post(`/admin/billing/refund/${transactionId}`, { amount }),
  adjustCredits: (orgId, amount, reason) =>
    api.post(`/admin/billing/credits/${orgId}`, { amount, reason }),

  // Credit Management
  getCreditPackages: () => api.get("/admin/credits/packages"),
  getOrganizationCredits: (orgId) =>
    api.get(`/admin/organisations/${orgId}/credits`),
  addOrganizationCredits: (orgId, data) =>
    api.post(`/admin/organisations/${orgId}/credits`, data),
  setOrganizationCredits: (orgId, data) =>
    api.put(`/admin/organisations/${orgId}/credits`, data),
  deductOrganizationCredits: (orgId, data) =>
    api.delete(`/admin/organisations/${orgId}/credits`, { data }),
  getOrganizationCreditHistory: (orgId, params) =>
    api.get(`/admin/organisations/${orgId}/credits/history`, { params }),

  // Invoices Management
  getInvoices: (params) => api.get("/admin/invoices", { params }),
  getInvoiceById: (id) => api.get(`/admin/invoices/${id}`),
  createInvoice: (data) => api.post("/admin/invoices", data),
  updateInvoice: (id, data) => api.patch(`/admin/invoices/${id}`, data),
  deleteInvoice: (id) => api.delete(`/admin/invoices/${id}`),
  sendInvoice: (id) => api.post(`/admin/invoices/${id}/send`),
  markInvoicePaid: (id, data) => api.post(`/admin/invoices/${id}/paid`, data),
  downloadInvoicePdf: (id) =>
    api.get(`/admin/invoices/${id}/pdf`, { responseType: "blob" }),

  // Billing/Invoice endpoints
  getUsageLog: (orgId, params) =>
    api.get(`/admin/organisations/${orgId}/usage-log`, { params }),
  getUnbilledUsage: (orgId, params) =>
    api.get(`/admin/organisations/${orgId}/unbilled-usage`, { params }),
  updateBillingMode: (orgId, data) =>
    api.put(`/admin/organisations/${orgId}/billing-mode`, data),
  getUsagePrices: (orgId) =>
    api.get(`/admin/organisations/${orgId}/usage-prices`),
  updateUsagePrices: (orgId, prices) =>
    api.put(`/admin/organisations/${orgId}/usage-prices`, { prices }),
  getTaxRates: () => api.get("/admin/tax-rates"),
  getCreditPricing: () => api.get("/admin/credit-pricing"),
  getInvoiceBilledOrgs: () => api.get("/admin/organisations-invoice-billed"),
  generateInvoiceItems: (orgId, data) =>
    api.post(`/admin/organisations/${orgId}/generate-items`, data),

  // System Management
  getSystemHealth: () => api.get("/admin/system/health"),
  getSystemLogs: (params) => api.get("/admin/system/logs", { params }),
  clearCache: () => api.post("/admin/system/clear-cache"),

  // Settings
  getSettings: () => api.get("/admin/settings"),
  updateSettings: (data) => api.patch("/admin/settings", data),

  // SMS Management
  getSMSDashboard: () => api.get("/admin/sms/dashboard"),
  getSMSMessages: (params) => api.get("/admin/sms/messages", { params }),
  getSMSMessage: (id) => api.get(`/admin/sms/messages/${id}`),
  resendSMS: (id) => api.post(`/admin/sms/messages/${id}/resend`),
  getSMSTemplates: () => api.get("/admin/sms/templates"),
  updateSMSTemplate: (id, data) => api.put(`/admin/sms/templates/${id}`, data),
  getSMSOptOuts: (params) => api.get("/admin/sms/opt-outs", { params }),
  removeSMSOptOut: (id) => api.delete(`/admin/sms/opt-outs/${id}`),
  getOrgSMSSettings: (orgId) =>
    api.get(`/admin/sms/organisations/${orgId}/settings`),
  updateOrgSMSSettings: (orgId, data) =>
    api.put(`/admin/sms/organisations/${orgId}/settings`, data),
  getSMSProviderStatus: () => api.get("/admin/sms/provider/status"),
  sendTestSMS: (data) => api.post("/admin/sms/test", data),
};

// Aliases for backward compatibility with Organizations.jsx
export const adminFeaturesApi = {
  listOrganizations: adminApi.getOrganizations,
  getOrganization: adminApi.getOrganizationById,
  updateOrganizationPlan: adminApi.updateOrganizationPlan,
  setFeatureOverride: adminApi.setFeatureOverride,
  removeFeatureOverride: adminApi.removeFeatureOverride,
  getOrganizationFeatures: adminApi.getOrganizationFeatures,
};

// ============================================================================
// CUSTOM PLAN API (Admin)
// ============================================================================
export const customPlanApi = {
  getOptions: () => api.get("/admin/custom-plans/options"),
  listOrganizations: () => api.get("/admin/custom-plans/organisations"),
  setCustomPlan: (organisationId, data) =>
    api.post(`/admin/custom-plans/organisations/${organisationId}`, data),
  getDetails: (organisationId) =>
    api.get(`/admin/custom-plans/organisations/${organisationId}`),
  updateFeatures: (organisationId, features, reason) =>
    api.patch(`/admin/custom-plans/organisations/${organisationId}/features`, {
      features,
      reason,
    }),
  updateLimits: (organisationId, limits, reason) =>
    api.patch(`/admin/custom-plans/organisations/${organisationId}/limits`, {
      limits,
      reason,
    }),
  updatePrice: (organisationId, price, billingInterval, reason) =>
    api.patch(`/admin/custom-plans/organisations/${organisationId}/price`, {
      price,
      billingInterval,
      reason,
    }),
  enableFeature: (organisationId, feature, expiresAt, reason) =>
    api.post(
      `/admin/custom-plans/organisations/${organisationId}/features/${feature}/enable`,
      { expiresAt, reason },
    ),
  disableFeature: (organisationId, feature, reason) =>
    api.post(
      `/admin/custom-plans/organisations/${organisationId}/features/${feature}/disable`,
      { reason },
    ),
  setLimit: (organisationId, limitKey, value, reason) =>
    api.patch(
      `/admin/custom-plans/organisations/${organisationId}/limits/${limitKey}`,
      { value, reason },
    ),
  getHistory: (organisationId, limit = 50) =>
    api.get(`/admin/custom-plans/organisations/${organisationId}/history`, {
      params: { limit },
    }),
  revertToStandard: (organisationId, newPlan, reason) =>
    api.post(`/admin/custom-plans/organisations/${organisationId}/revert`, {
      newPlan,
      reason,
    }),
};

// ============================================================================
// PDF TOOLS API
// ============================================================================
export const pdfToolsApi = {
  merge: (documentIds) => api.post("/pdf-tools/merge", { documentIds }),
  split: (documentId, pages) =>
    api.post("/pdf-tools/split", { documentId, pages }),
  compress: (documentId) => api.post("/pdf-tools/compress", { documentId }),
  rotate: (documentId, rotation) =>
    api.post("/pdf-tools/rotate", { documentId, rotation }),
  addWatermark: (documentId, watermark) =>
    api.post("/pdf-tools/watermark", { documentId, watermark }),
  convertToPdf: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/pdf-tools/convert", formData);
  },
  extractPages: (documentId, pages) =>
    api.post("/pdf-tools/extract", { documentId, pages }),
  addPageNumbers: (documentId, options) =>
    api.post("/pdf-tools/page-numbers", { documentId, ...options }),
};

// ============================================================================
// ADVANCED SECURITY API
// ============================================================================
export const advancedSecurityApi = {
  getSettings: () => api.get("/security/settings"),
  updateSettings: (data) => api.patch("/security/settings", data),
  getAuditLog: (params) => api.get("/security/events", { params }),
  getSessions: () => api.get("/security/sessions"),
  revokeSession: (sessionId) => api.delete(`/security/sessions/${sessionId}`),
  revokeAllSessions: () => api.delete("/security/sessions"),
  getLoginHistory: (params) => api.get("/security/events", { params }),
  enableIpWhitelist: (ips) => api.post("/security/ip-whitelist", { ips }),
  disableIpWhitelist: () => api.delete("/security/ip-whitelist"),
  getIpWhitelist: () => api.get("/security/ip-whitelist"),
  enableDeviceTracking: () => api.post("/security/devices"),
  disableDeviceTracking: () => api.delete("/security/devices"),
  getTrustedDevices: () => api.get("/security/devices"),
  removeTrustedDevice: (fingerprint) =>
    api.delete(`/security/devices/${fingerprint}`),
  getBackupCodesStatus: () => api.get("/security/backup-codes/status"),
  generateBackupCodes: () => api.post("/security/backup-codes/generate"),
  getWebAuthnCredentials: () => api.get("/security/webauthn/credentials"),
  getWebAuthnRegistrationOptions: () =>
    api.get("/security/webauthn/register/options"),
  verifyWebAuthnRegistration: (data) =>
    api.post("/security/webauthn/register/verify", data),
  deleteWebAuthnCredential: (id) =>
    api.delete(`/security/webauthn/credentials/${id}`),
  renameWebAuthnCredential: (id, data) =>
    api.patch(`/security/webauthn/credentials/${id}`, data),
  getIpAllowlistSettings: () => api.get("/security/ip-allowlist"),
  setIpAllowlistEnabled: (data) =>
    api.post("/security/ip-allowlist/enabled", data),
  addIpEntry: (data) => api.post("/security/ip-allowlist/entries", data),
  updateIpEntry: (id, data) =>
    api.patch(`/security/ip-allowlist/entries/${id}`, data),
  deleteIpEntry: (id) => api.delete(`/security/ip-allowlist/entries/${id}`),
  getIpAccessLog: () => api.get("/security/ip-allowlist/log"),
  getCurrentIp: () => api.get("/security/ip-allowlist/check"),
};

// ============================================================================
// PAYMENTS API (for regular users)
// ============================================================================
export const paymentsApi = {
  getPaymentMethods: () => api.get("/payments/methods"),
  addPaymentMethod: (data) => api.post("/payments/methods", data),
  removePaymentMethod: (id) => api.delete(`/payments/methods/${id}`),
  setDefaultPaymentMethod: (id) => api.post(`/payments/methods/${id}/default`),
  getInvoices: (params) => api.get("/payments/invoices", { params }),
  getInvoice: (id) => api.get(`/payments/invoices/${id}`),
  downloadInvoice: (id) =>
    api.get(`/payments/invoices/${id}/download`, { responseType: "blob" }),
  getSubscription: () => api.get("/payments/subscription"),
  updateSubscription: (data) => api.patch("/payments/subscription", data),
  cancelSubscription: (reason) =>
    api.post("/payments/subscription/cancel", { reason }),
  reactivateSubscription: () => api.post("/payments/subscription/reactivate"),
  createCheckoutSession: (priceId) =>
    api.post("/payments/checkout", { priceId }),
  getBillingPortalUrl: () => api.get("/payments/billing-portal"),
  getSettings: () => api.get("/payment-collection/settings"),
  updateSettings: (data) => api.patch("/payment-collection/settings", data),
  createOnboardingLink: () => api.post("/payment-collection/connect"),
  disconnect: () => api.delete("/payment-collection/connect"),
  getDashboardLink: () => api.get("/payment-collection/dashboard-link"),
  getStats: () => api.get("/payment-collection/stats"),
  getHistory: (params) => api.get("/payment-collection/history", { params }),
};

// ============================================================================
// ORGANIZATION API (for team/org management by org admins)
// ============================================================================
export const organisationApi = {
  get: () => api.get("/team/organisation"),
  update: (data) => api.patch("/team/organisation", data),
  create: (data) => api.post("/team/organisation", data),
  getMembers: () => api.get("/team/members"),
  inviteMember: (data) => api.post("/team/invitations", data),
  removeMember: (memberId) => api.delete(`/team/members/${memberId}`),
  updateMemberRole: (memberId, role) =>
    api.patch(`/team/members/${memberId}/role`, { role }),
  getInvitations: () => api.get("/team/invitations"),
  cancelInvitation: (invitationId) =>
    api.delete(`/team/invitations/${invitationId}`),
  resendInvitation: (invitationId) =>
    api.post(`/team/invitations/${invitationId}/resend`),
  leave: () => api.post("/team/leave"),
  transferOwnership: (memberId) =>
    api.post("/team/transfer-ownership", { memberId }),
};

// ============================================================================
// EMBED API
// ============================================================================
export const embedApi = {
  getConfig: () => api.get("/embed/config"),
  updateConfig: (data) => api.patch("/embed/config", data),
  generateToken: (envelopeId) => api.post("/embed/token", { envelopeId }),
  getTheme: () => api.get("/embed/theme"),
  updateTheme: (data) => api.patch("/embed/theme", data),
};

// ============================================================================
// CONTACTS API
// ============================================================================
export const contactsApi = {
  // Contacts CRUD
  list: (params) => api.get("/contacts", { params }),
  create: (data) => api.post("/contacts", data),
  get: (id) => api.get(`/contacts/${id}`),
  update: (id, data) => api.patch(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),

  // Search & autocomplete
  search: (q, limit = 10) =>
    api.get("/contacts/search", { params: { q, limit } }),

  // Favorites
  toggleFavorite: (id) => api.post(`/contacts/${id}/favorite`),

  // History
  getHistory: (id, params) => api.get(`/contacts/${id}/history`, { params }),

  // Plan limits
  getLimits: () => api.get("/contacts/limits"),

  // Import/Export
  import: (contacts) => api.post("/contacts/import", { contacts }),
  export: () => api.get("/contacts/export"),

  // Groups
  listGroups: () => api.get("/contacts/groups"),
  createGroup: (data) => api.post("/contacts/groups", data),
  getGroup: (id) => api.get(`/contacts/groups/${id}`),
  updateGroup: (id, data) => api.patch(`/contacts/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/contacts/groups/${id}`),

  // Group members
  getGroupMembers: (groupId, params) =>
    api.get(`/contacts/groups/${groupId}/members`, { params }),
  addToGroup: (groupId, contactId) =>
    api.post(`/contacts/groups/${groupId}/members`, { contactId }),
  removeFromGroup: (groupId, contactId) =>
    api.delete(`/contacts/groups/${groupId}/members/${contactId}`),

  // Set contact's groups
  setContactGroups: (contactId, groupIds) =>
    api.put(`/contacts/${contactId}/groups`, { groupIds }),

  // Track usage
  trackUsage: (id) => api.post(`/contacts/${id}/usage`),
};

// ============================================================================
// NOTIFICATIONS API
// ============================================================================
export const notificationsApi = {
  list: (params) => api.get("/notifications", { params }),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch("/notifications/read-all"),
  getPreferences: () => api.get("/notifications/preferences"),
  updatePreferences: (data) => api.patch("/notifications/preferences", data),
  getUnreadCount: () => api.get("/notifications/unread-count"),
  delete: (id) => api.delete(`/notifications/${id}`),
  deleteAll: () => api.delete("/notifications"),
};

// ============================================================================
// REPORTS API
// ============================================================================
export const reportsApi = {
  generate: (type, params) =>
    api.post("/reports/generate", { type, ...params }),
  list: () => api.get("/reports"),
  download: (id) =>
    api.get(`/reports/${id}/download`, { responseType: "blob" }),
  schedule: (data) => api.post("/reports/schedule", data),
  getScheduled: () => api.get("/reports/scheduled"),
  deleteScheduled: (id) => api.delete(`/reports/scheduled/${id}`),
};

// ============================================================================
// AUDIT API
// ============================================================================
export const auditApi = {
  getLog: (params) => api.get("/audit", { params }),
  getEnvelopeHistory: (envelopeId) => api.get(`/audit/envelope/${envelopeId}`),
  getUserActivity: (userId, params) =>
    api.get(`/audit/user/${userId}`, { params }),
  export: (params) =>
    api.get("/audit/export", { params, responseType: "blob" }),
};

// ============================================================================
// INTEGRATIONS API
// ============================================================================
export const integrationsApi = {
  list: () => api.get("/integrations"),
  getById: (id) => api.get(`/integrations/${id}`),
  connect: (provider, data) =>
    api.post(`/integrations/${provider}/connect`, data),
  disconnect: (provider) => api.delete(`/integrations/${provider}`),
  getSettings: (provider) => api.get(`/integrations/${provider}/settings`),
  updateSettings: (provider, data) =>
    api.patch(`/integrations/${provider}/settings`, data),
  sync: (provider) => api.post(`/integrations/${provider}/sync`),
};

// ============================================================================
// STORAGE API
// ============================================================================
export const storageApi = {
  getUsage: () => api.get("/storage/usage"),
  listFiles: (params) => api.get("/storage/files", { params }),
  deleteFile: (fileId) => api.delete(`/storage/files/${fileId}`),
  getQuota: () => api.get("/storage/quota"),
};

// ============================================================================
// ENVELOPE API
// ============================================================================
export const envelopeApi = {
  create: (data) => api.post("/envelopes", data),
  list: (params) => api.get("/envelopes", { params }),
  getById: (id) => api.get(`/envelopes/${id}`),
  update: (id, data) => api.patch(`/envelopes/${id}`, data),
  delete: (id) => api.delete(`/envelopes/${id}`),
  getStats: () => api.get("/envelopes/stats"),
  uploadDocument: (envelopeId, file, onProgress) => {
    const formData = new FormData();
    formData.append("document", file);
    return api.post(`/envelopes/${envelopeId}/documents`, formData, {
      onUploadProgress: onProgress,
    });
  },
  removeDocument: (envelopeId, documentId) =>
    api.delete(`/envelopes/${envelopeId}/documents/${documentId}`),
  downloadDocument: (envelopeId, documentId, type = "original") =>
    api.get(`/envelopes/${envelopeId}/documents/${documentId}/download`, {
      params: { type },
      responseType: "blob",
    }),
  addRecipients: (envelopeId, recipients) =>
    api.post(`/envelopes/${envelopeId}/recipients`, { recipients }),
  updateRecipient: (envelopeId, recipientId, data) =>
    api.patch(`/envelopes/${envelopeId}/recipients/${recipientId}`, data),
  reorderRecipients: (envelopeId, order) =>
    api.put(`/envelopes/${envelopeId}/recipients/reorder`, { order }),
  removeRecipient: (envelopeId, recipientId) =>
    api.delete(`/envelopes/${envelopeId}/recipients/${recipientId}`),
  resendToRecipient: (envelopeId, recipientId, message) =>
    api.post(`/envelopes/${envelopeId}/recipients/${recipientId}/resend`, {
      message,
    }),
  addFields: (envelopeId, documentId, fields) =>
    api.post(`/envelopes/${envelopeId}/documents/${documentId}/fields`, {
      fields,
    }),
  updateField: (envelopeId, documentId, fieldId, data) =>
    api.patch(
      `/envelopes/${envelopeId}/documents/${documentId}/fields/${fieldId}`,
      data,
    ),
  removeField: (envelopeId, documentId, fieldId) =>
    api.delete(
      `/envelopes/${envelopeId}/documents/${documentId}/fields/${fieldId}`,
    ),
  bulkUpdateFields: (envelopeId, documentId, fields) =>
    api.put(`/envelopes/${envelopeId}/documents/${documentId}/fields`, {
      fields,
    }),
  send: (envelopeId, body = {}) =>
    api.post(`/envelopes/${envelopeId}/send`, body),
  void: (envelopeId, reason) =>
    api.post(`/envelopes/${envelopeId}/void`, { reason }),
  duplicate: (envelopeId) => api.post(`/envelopes/${envelopeId}/duplicate`),
  downloadCombined: (envelopeId, type = "signed") =>
    api.get(`/envelopes/${envelopeId}/download`, {
      params: { type },
      responseType: "blob",
    }),
  getAuditTrail: (envelopeId) =>
    api.get(`/envelopes/${envelopeId}/audit-trail`),
  downloadAuditTrail: (envelopeId) =>
    api.get(`/envelopes/${envelopeId}/audit-trail/download`, {
      responseType: "blob",
    }),
  getCertificate: (envelopeId) =>
    api.get(`/envelopes/${envelopeId}/certificate`),
  downloadCertificate: (envelopeId) =>
    api.get(`/envelopes/${envelopeId}/certificate/download`, {
      responseType: "blob",
    }),
  correct: (envelopeId) => api.post(`/envelopes/${envelopeId}/correct`),
  finishCorrection: (envelopeId) =>
    api.post(`/envelopes/${envelopeId}/finish-correction`),
  remind: (envelopeId) => api.post(`/envelopes/${envelopeId}/remind`),
  // Import fillable PDF form fields as eScribe fields
  importFormFields: (envelopeId, documentId, recipientId = null) =>
    api.post(
      `/envelopes/${envelopeId}/documents/${documentId}/import-form-fields`,
      {
        recipientId,
      },
    ),
  // Export audit log
  exportAuditLog: (envelopeId, format = "csv") =>
    api.get(`/envelopes/${envelopeId}/audit-log`, {
      params: { format },
      responseType: format === "csv" ? "blob" : "json",
    }),
  resendAll: (envelopeId) => api.post(`/envelopes/${envelopeId}/remind`),
  // Notes
  getNotes: (envelopeId) => api.get(`/envelopes/${envelopeId}/notes`),
  addNote: (envelopeId, content) =>
    api.post(`/envelopes/${envelopeId}/notes`, { content }),
  updateNote: (envelopeId, noteId, content) =>
    api.patch(`/envelopes/${envelopeId}/notes/${noteId}`, { content }),
  deleteNote: (envelopeId, noteId) =>
    api.delete(`/envelopes/${envelopeId}/notes/${noteId}`),
};

// ============================================================================
// TEMPLATES API
// ============================================================================
export const templateApi = {
  list: (params) => api.get("/templates", { params }),
  listPublic: (params) => api.get("/templates/public", { params }),
  getById: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post("/templates", data),
  createWithDocument: (formData) => api.post("/templates", formData),
  update: (id, data) => api.patch(`/templates/${id}`, data),
  updateWithDocument: (id, formData) => api.put(`/templates/${id}`, formData),
  delete: (id) => api.delete(`/templates/${id}`),
  duplicate: (id) => api.post(`/templates/${id}/duplicate`),
  createFromEnvelope: (envelopeId, data) =>
    api.post(`/templates/from-envelope/${envelopeId}`, data),
  getDocument: (templateId) =>
    api.get(`/templates/${templateId}/document`, {
      responseType: "blob",
    }),
  uploadDocument: (templateId, file) => {
    const formData = new FormData();
    formData.append("document", file);
    return api.post(`/templates/${templateId}/documents`, formData);
  },
  removeDocument: (templateId, documentId) =>
    api.delete(`/templates/${templateId}/documents/${documentId}`),
  addFields: (templateId, fields) =>
    api.post(`/templates/${templateId}/fields`, {
      fields,
    }),
  updateFields: (templateId, fields) =>
    api.put(`/templates/${templateId}/fields`, {
      fields,
    }),
  addRoles: (templateId, roles) =>
    api.post(`/templates/${templateId}/roles`, { roles }),
  updateRoles: (templateId, roles) =>
    api.put(`/templates/${templateId}/roles`, { roles }),
  use: (templateId, data) => api.post(`/templates/${templateId}/use`, data),
  getCategories: () => api.get("/templates/categories"),
  share: (id, data) => api.post(`/templates/${id}/share`, data),
  unshare: (id) => api.delete(`/templates/${id}/share`),
  // Branding
  uploadBrandingLogo: (templateId, file, type = "logo") => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(
      `/templates/${templateId}/branding/logo?type=${type}`,
      formData,
      {
        headers: { "Content-Type": undefined },
      },
    );
  },
};

// ============================================================================
// SIGNING API (public endpoints for signers)
// ============================================================================
export const signingApi = {
  getEnvelope: (token) => publicApi.get(`/sign/${token}`),
  accessSession: (token, accessCode) =>
    publicApi.post(`/sign/${token}/access`, { accessCode }),
  recordConsent: (token) => publicApi.post(`/sign/${token}/consent`),
  submitSignature: (token, data) => publicApi.post(`/sign/${token}`, data),
  declineEnvelope: (token, reason) =>
    publicApi.post(`/sign/${token}/decline`, { reason }),
  getDocument: (token, documentId) =>
    publicApi.get(`/sign/${token}/documents/${documentId}`, {
      responseType: "blob",
    }),
  fillField: (token, fieldId, value) =>
    publicApi.post(`/sign/${token}/fields/${fieldId}`, { value }),
  applySignature: (token, fieldId, data) =>
    publicApi.post(`/sign/${token}/fields/${fieldId}/signature`, data),
  completeSigning: (token, data) =>
    publicApi.post(`/sign/${token}/complete`, data),
  declineSigning: (token, reason) =>
    publicApi.post(`/sign/${token}/decline`, { reason }),
  delegateSignature: (token, data) =>
    publicApi.post(`/sign/${token}/delegate`, data),
  verifyAccess: (token) => publicApi.get(`/sign/${token}/verify`),
  confirmIdentity: (token, data) =>
    publicApi.post(`/sign/${token}/confirm-identity`, data),

  // In-Person Signing - maps to /api/v1/in-person routes
  getInPersonSigningData: (sessionToken) =>
    publicApi.get(`/in-person/session/${sessionToken}/status`),
  submitInPersonSignature: (sessionToken, recipientId, data) =>
    publicApi.post(
      `/in-person/session/${sessionToken}/complete/${recipientId}`,
      data,
    ),
};

// ============================================================================
// VERIFICATION API (public)
// ============================================================================
export const verificationApi = {
  verifyDocument: (file) => {
    const formData = new FormData();
    formData.append("document", file);
    return publicApi.post("/verify", formData);
  },
  verifyEnvelope: (envelopeId) => publicApi.get(`/verify/${envelopeId}`),
  verifyByHash: (hash) => publicApi.post("/verify/hash", { hash }),
  getAuditTrail: (envelopeId) =>
    publicApi.get(`/verify/${envelopeId}/audit`),
  // Downloads are email-challenged: pass the email of a party to the
  // envelope (recipient or sender); the server rejects others with 403
  getSignedDocument: (envelopeId, email) =>
    publicApi.post(
      `/verify/${envelopeId}/document`,
      { email },
      { responseType: "blob" },
    ),
  getCertificate: (envelopeId, email) =>
    publicApi.post(`/verify/${envelopeId}/certificate`, { email }, {
      responseType: "blob",
    }),
  verifyCertificate: (certificateId) =>
    publicApi.get(`/verify/certificate/${certificateId}`),
  getPublicAuditTrail: (auditId) =>
    publicApi.get(`/verify/audit/${auditId}`),
};

// ============================================================================
// SEARCH API
// ============================================================================
export const searchApi = {
  globalSearch: (query, limit = 3) =>
    api.get("/search", { params: { q: query, limit } }),
  searchEnvelopes: (query, limit = 10) =>
    api.get("/search/envelopes", { params: { q: query, limit } }),
  searchTemplates: (query, limit = 10) =>
    api.get("/search/templates", { params: { q: query, limit } }),
};

// ============================================================================
// API KEYS API
// ============================================================================
export const apiKeysApi = {
  list: () => api.get("/api-keys"),
  create: (data) => api.post("/api-keys", data),
  getById: (id) => api.get(`/api-keys/${id}`),
  update: (id, data) => api.patch(`/api-keys/${id}`, data),
  delete: (id) => api.delete(`/api-keys/${id}`),
  revoke: (id) => api.post(`/api-keys/${id}/revoke`),
  reactivate: (id) => api.post(`/api-keys/${id}/reactivate`),
  regenerate: (id) => api.post(`/api-keys/${id}/regenerate`),
  getUsage: (id, params) => api.get(`/api-keys/${id}/usage`, { params }),
  getLogs: (id, params) => api.get(`/api-keys/${id}/logs`, { params }),
};

// ============================================================================
// FOLDERS API
// ============================================================================
export const foldersApi = {
  list: () => api.get("/folders"),
  create: (data) => api.post("/folders", data),
  getById: (id) => api.get(`/folders/${id}`),
  update: (id, data) => api.patch(`/folders/${id}`, data),
  delete: (id) => api.delete(`/folders/${id}`),
  move: (id, parentId) => api.post(`/folders/${id}/move`, { parentId }),
  getContents: (id, params) => api.get(`/folders/${id}/contents`, { params }),
  moveItems: (folderId, items) =>
    api.post(`/folders/${folderId}/items`, { items }),
};

// ============================================================================
// TAGS API
// ============================================================================
export const tagsApi = {
  list: () => api.get("/tags"),
  create: (data) => api.post("/tags", data),
  update: (id, data) => api.patch(`/tags/${id}`, data),
  delete: (id) => api.delete(`/tags/${id}`),
  getEnvelopeTags: (envelopeId) => api.get(`/tags/envelope/${envelopeId}`),
  addToEnvelope: (envelopeId, tagId) =>
    api.post(`/tags/envelope/${envelopeId}/${tagId}`),
  removeFromEnvelope: (envelopeId, tagId) =>
    api.delete(`/tags/envelope/${envelopeId}/${tagId}`),
  setEnvelopeTags: (envelopeId, tagIds) =>
    api.put(`/tags/envelope/${envelopeId}`, { tagIds }),
  addToTemplate: (templateId, tagIds) =>
    api.post(`/tags/template/${templateId}`, { tagIds }),
  removeFromTemplate: (templateId, tagId) =>
    api.delete(`/tags/template/${templateId}/${tagId}`),
};

// ============================================================================
// TEAM API
// ============================================================================
export const teamApi = {
  getOrganization: () => api.get("/team/organisation"),
  updateOrganization: (data) => api.patch("/team/organisation", data),
  getMembers: () => api.get("/team/members"),
  inviteMember: (data) => api.post("/team/invitations", data),
  updateMember: (memberId, data) =>
    api.patch(`/team/members/${memberId}`, data),
  removeMember: (memberId) => api.delete(`/team/members/${memberId}`),
  updateMemberRole: (memberId, role) =>
    api.patch(`/team/members/${memberId}`, { role }),
  getInvitations: () => api.get("/team/invitations"),
  cancelInvitation: (invitationId) =>
    api.delete(`/team/invitations/${invitationId}`),
  resendInvitation: (invitationId) =>
    api.post(`/team/invitations/${invitationId}/resend`),
  acceptInvitation: (token) => api.post(`/team/invitations/${token}/accept`),
  declineInvitation: (token) => api.post(`/team/invitations/${token}/decline`),
  getActivityLog: (params) => api.get("/team/activity", { params }),
  getInvitationDetails: (token) => api.get(`/team/invitations/${token}`),
};

// ============================================================================
// BULK SEND API
// ============================================================================
export const bulkSendApi = {
  create: (data) => api.post("/bulk-send", data),
  list: (params) => api.get("/bulk-send", { params }),
  getById: (id) => api.get(`/bulk-send/${id}`),
  update: (id, data) => api.patch(`/bulk-send/${id}`, data),
  delete: (id) => api.delete(`/bulk-send/${id}`),
  start: (id) => api.post(`/bulk-send/${id}/start`),
  pause: (id) => api.post(`/bulk-send/${id}/pause`),
  resume: (id) => api.post(`/bulk-send/${id}/resume`),
  cancel: (id) => api.post(`/bulk-send/${id}/cancel`),
  getRecipients: (id, params) =>
    api.get(`/bulk-send/${id}/recipients`, { params }),
  addRecipients: (id, recipients) =>
    api.post(`/bulk-send/${id}/recipients`, { recipients }),
  removeRecipient: (id, recipientId) =>
    api.delete(`/bulk-send/${id}/recipients/${recipientId}`),
  previewEmail: (id, recipientId) =>
    api.get(`/bulk-send/${id}/recipients/${recipientId}/preview`),
  downloadTemplate: () =>
    api.get("/bulk-send/template", { responseType: "blob" }),
  validateCsv: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/bulk-send/validate-csv", formData);
  },
};

// ============================================================================
// ANALYTICS API
// ============================================================================
export const analyticsApi = {
  getDashboard: (params) => api.get("/analytics/dashboard", { params }),
  getEnvelopeStats: (params) => api.get("/analytics/envelopes", { params }),
  getEnvelopeAnalytics: (params) => api.get("/analytics/envelopes", { params }),
  getBottlenecks: (params) => api.get("/analytics/bottlenecks", { params }),
  getSigningTimes: (params) => api.get("/analytics/signing-times", { params }),
  getCompletionRates: (params) =>
    api.get("/analytics/completion-rates", { params }),
  getTopTemplates: (params) => api.get("/analytics/top-templates", { params }),
  getActivityTimeline: (params) => api.get("/analytics/activity", { params }),
  getRecipientStats: (params) => api.get("/analytics/recipients", { params }),
  getTrends: (params) => api.get("/analytics/trends", { params }),
  getBusiestTimes: (params) => api.get("/analytics/busiest-times", { params }),
  getFunnel: (params) => api.get("/analytics/funnel", { params }),
  export: (params) =>
    api.get("/analytics/export", { params, responseType: "blob" }),
};

// ============================================================================
// SIGNING LINKS API
// ============================================================================
export const signingLinksApi = {
  list: (params) => api.get("/signing-links", { params }),
  create: (envelopeId, recipientId, data) =>
    api.post(
      `/signing-links/envelope/${envelopeId}/recipient/${recipientId}`,
      data,
    ),
  createForAll: (envelopeId, data) =>
    api.post(`/signing-links/envelope/${envelopeId}/all`, data),
  getByEnvelope: (envelopeId) =>
    api.get(`/signing-links/envelope/${envelopeId}`),
  getById: (id) => api.get(`/signing-links/${id}`),
  update: (id, data) => api.patch(`/signing-links/${id}`, data),
  delete: (id) => api.delete(`/signing-links/${id}`),
  disable: (id) => api.post(`/signing-links/${id}/deactivate`),
  deactivate: (id) => api.post(`/signing-links/${id}/deactivate`),
  enable: (id) => api.post(`/signing-links/${id}/enable`),
  regenerate: (id) => api.post(`/signing-links/${id}/regenerate`),
  getStats: (envelopeId) =>
    api.get(`/signing-links/envelope/${envelopeId}/stats`),
  getSubmissions: (id, params) =>
    api.get(`/signing-links/${id}/submissions`, { params }),
  getPublicInfo: (token) => api.get(`/signing-links/public/${token}`),
  getPublic: (token) => api.get(`/signing-links/public/${token}`), // Alias
  validate: (token, data) => api.post(`/signing-links/validate/${token}`, data),
  submitPublic: (token, data) =>
    api.post(`/signing-links/validate/${token}`, data),
  getQRCode: (token) => api.get(`/signing-links/qr/${token}`),
  deactivateAll: (envelopeId) =>
    api.post(`/signing-links/envelope/${envelopeId}/deactivate-all`),
};

// ============================================================================
// COMMENTS API
// ============================================================================
export const commentsApi = {
  list: (envelopeId, documentId) =>
    documentId
      ? api.get(`/comments/envelope/${envelopeId}/document/${documentId}`)
      : api.get(`/comments/envelope/${envelopeId}`),
  create: (envelopeId, documentId, data) =>
    api.post(`/comments/envelope/${envelopeId}/document/${documentId}`, data),
  update: (commentId, data) => api.patch(`/comments/${commentId}`, data),
  delete: (commentId) => api.delete(`/comments/${commentId}`),
  toggleResolved: (commentId) => api.post(`/comments/${commentId}/resolve`),
  reply: (commentId, data) => api.post(`/comments/${commentId}/reply`, data),
  getThread: (commentId) => api.get(`/comments/${commentId}/thread`),
  addRecipientComment: (envelopeId, documentId, recipientId, data) =>
    publicApi.post(
      `/comments/recipient/${envelopeId}/${documentId}/${recipientId}`,
      data,
    ),
  // Public endpoints for signers
  getSignerComments: (token) => publicApi.get(`/comments/signer/${token}`),
  addSignerComment: (token, data) =>
    publicApi.post(`/comments/signer/${token}`, data),
};

// ============================================================================
// IN-PERSON SIGNING API
// ============================================================================
export const inPersonApi = {
  create: (envelopeId, data) =>
    api.post(`/in-person/envelope/${envelopeId}/start`, data),
  start: (envelopeId, data) =>
    api.post(`/in-person/envelope/${envelopeId}/start`, data),
  list: (params) => api.get("/in-person/sessions", { params }),
  getById: (id) => api.get(`/in-person/sessions/${id}`),
  pause: (id) => api.post(`/in-person/sessions/${id}/pause`),
  resume: (id) => api.post(`/in-person/sessions/${id}/resume`),
  cancel: (id, reason) =>
    api.post(`/in-person/sessions/${id}/cancel`, { reason }),
  getStatus: (sessionToken, pin) => {
    if (pin) {
      return publicApi.get(`/in-person/session/${sessionToken}/status`, {
        params: { pin },
      });
    }
    return publicApi.get(`/in-person/session/${sessionToken}/status`);
  },
  handoff: (sessionToken, recipientId, pin) =>
    publicApi.post(
      `/in-person/session/${sessionToken}/handoff/${recipientId}`,
      {
        pin,
      },
    ),
  completeRecipient: (sessionToken, recipientId, data = {}) =>
    publicApi.post(
      `/in-person/session/${sessionToken}/complete/${recipientId}`,
      data,
    ),
  startSignerTurn: (id, recipientId) =>
    publicApi.post(`/in-person/session/${id}/handoff/${recipientId}`),
  completeSignerTurn: (id, recipientId) =>
    publicApi.post(`/in-person/session/${id}/complete/${recipientId}`),
  complete: (id) => api.post(`/in-person/sessions/${id}/complete`),
  verifyPin: (sessionId, pin) =>
    publicApi.get(`/in-person/session/${sessionId}/status`, {
      params: { pin },
    }),
  getPublic: (sessionId) =>
    publicApi.get(`/in-person/session/${sessionId}/status`),
};

// ============================================================================
// CLAUSE LIBRARY API
// ============================================================================
export const clauseApi = {
  list: (params) => api.get("/clauses", { params }),
  getById: (id) => api.get(`/clauses/${id}`),
  create: (data) => api.post("/clauses", data),
  update: (id, data) => api.patch(`/clauses/${id}`, data),
  delete: (id) => api.delete(`/clauses/${id}`),
  use: (id) => api.post(`/clauses/${id}/use`),
  approve: (id) => api.post(`/clauses/${id}/approve`),
  getVersions: (id) => api.get(`/clauses/${id}/history`),
  createVersion: (id, data) => api.post(`/clauses/${id}/version`, data),
  revertToVersion: (id, versionId) =>
    api.post(`/clauses/${id}/revert/${versionId}`),
  trackUsage: (id) => api.post(`/clauses/${id}/usage`),
  getCategories: () => api.get("/clauses/categories"),
  createCategory: (data) => api.post("/clauses/categories", data),
  updateCategory: (id, data) => api.patch(`/clauses/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/clauses/categories/${id}`),
  getTags: () => api.get("/clauses/tags/popular"),
};

// ============================================================================
// BRANDING API
// ============================================================================
export const brandingApi = {
  get: () => api.get("/branding"),
  update: (data) => api.patch("/branding", data),
  uploadLogo: (type, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(
      `/branding/logo?type=${encodeURIComponent(type)}`,
      formData,
      {
        headers: { "Content-Type": undefined },
      },
    );
  },
  deleteLogo: (type) => api.delete(`/branding/logo/${type}`),
  getPreview: () => api.get("/branding/preview"),
  reset: () => api.post("/branding/reset"),
};

// ============================================================================
// REMINDERS API
// ============================================================================
export const reminderApi = {
  // Dashboard
  getPending: () => api.get("/reminders/pending"),

  // Envelope schedules
  getEffective: (envelopeId) => api.get(`/reminders/envelope/${envelopeId}`),
  createForEnvelope: (envelopeId, data) =>
    api.post(`/reminders/envelope/${envelopeId}`, data),
  getHistory: (envelopeId) =>
    api.get(`/reminders/envelope/${envelopeId}/history`),

  // Send reminders
  sendImmediate: (envelopeId, recipientId) =>
    api.post(`/reminders/envelope/${envelopeId}/recipient/${recipientId}/send`),
  sendToAll: (envelopeId) =>
    api.post(`/reminders/envelope/${envelopeId}/send-all`),

  // Template schedules
  createForTemplate: (templateId, data) =>
    api.post(`/reminders/template/${templateId}`, data),

  // Organisation defaults
  createOrgDefault: (data) => api.post("/reminders/default", data),
  getOrgDefault: () => api.get("/reminders/default"),
};

// ============================================================================
// WEBHOOKS API
// ============================================================================
export const webhooksApi = {
  list: () => api.get("/webhooks"),
  create: (data) => api.post("/webhooks", data),
  getById: (id) => api.get(`/webhooks/${id}`),
  update: (id, data) => api.patch(`/webhooks/${id}`, data),
  delete: (id) => api.delete(`/webhooks/${id}`),
  test: (id) => api.post(`/webhooks/${id}/test`),
  getDeliveries: (id, params) =>
    api.get(`/webhooks/${id}/deliveries`, { params }),
  retryDelivery: (id, deliveryId) =>
    api.post(`/webhooks/${id}/deliveries/${deliveryId}/retry`),
  getEvents: () => api.get("/webhooks/events"),
};

// ============================================================================
// BILLING API
// ============================================================================
export const billingApi = {
  // ============================================
  // Plans
  // ============================================
  getPlans: () => api.get("/billing/plans"),

  // ============================================
  // Subscription
  // ============================================
  getSubscription: () => api.get("/billing/subscription"),
  createCheckoutSession: (planId, interval) =>
    api.post("/billing/checkout", { planId, interval }),
  changePlan: (planId, interval) =>
    api.post("/billing/change-plan", { planId, interval }),
  cancelSubscription: (immediately = false) =>
    api.post("/billing/cancel", { immediately }),
  resumeSubscription: () => api.post("/billing/resume"),

  // ============================================
  // Portal & Payment
  // ============================================
  createPortalSession: (returnUrl) =>
    api.post("/billing/portal", { returnUrl }),
  getPaymentMethods: () => api.get("/billing/payment-methods"),
  createSetupIntent: () => api.post("/billing/payment-methods/setup"),
  setDefaultPaymentMethod: (paymentMethodId) =>
    api.put("/billing/payment-methods/default", { paymentMethodId }),
  deletePaymentMethod: (id) => api.delete(`/billing/payment-methods/${id}`),

  // ============================================
  // Invoices
  // ============================================
  getInvoices: (limit = 20) =>
    api.get("/billing/invoices", { params: { limit } }),

  // ============================================
  // Billing Info
  // ============================================
  updateBillingInfo: (data) => api.put("/billing/info", data),

  // ============================================
  // Credits
  // ============================================

  /**
   * Get current credit balances
   * @returns {Promise<{ sms: number, envelopes: number, idVerification: number }>}
   */
  getCredits: () => api.get("/billing/credits"),

  /**
   * Get available credit packages
   * @returns {Promise<Array<{ type, name, unit, packages }>>}
   */
  getCreditPackages: () => api.get("/billing/credits/packages"),

  /**
   * Get credit transaction history
   * @param {Object} params
   * @param {string} [params.type] - Filter by credit type ('sms', 'envelopes', 'idVerification')
   * @param {number} [params.limit=50] - Number of records
   * @param {number} [params.offset=0] - Offset for pagination
   * @returns {Promise<{ transactions: Array, total: number }>}
   */
  getCreditHistory: (params) => api.get("/billing/credits/history", { params }),

  /**
   * Create checkout session for purchasing credits
   * @param {string} packageId - Credit package ID (e.g., 'sms_500', 'env_100')
   * @returns {Promise<{ url: string, sessionId: string }>}
   */
  purchaseCredits: (packageId) =>
    api.post("/billing/credits/purchase", { packageId }),

  /**
   * Check if organisation has sufficient credits
   * @param {string} type - Credit type
   * @param {number} amount - Required amount
   * @returns {Promise<{ hasSufficient: boolean, type: string, required: number }>}
   */
  checkCredits: (type, amount) =>
    api.get("/billing/credits/check", { params: { type, amount } }),

  /**
   * Configure auto-refill settings
   * @param {string} type - Credit type
   * @param {Object} settings - { enabled, threshold, amount }
   * @returns {Promise<Object>}
   */
  configureAutoRefill: (type, settings) =>
    api.post("/billing/credits/auto-refill", { type, settings }),

  // ============================================
  // Add-ons (if you have these)
  // ============================================
  // Add-ons
  getAddons: () => api.get("/billing/addons"),
  getActiveAddons: () => api.get("/billing/addons/active"),
  createAddonCheckout: (addonId, tierId) =>
    api.post("/billing/addons/checkout", { addonId, tierId }),
  upgradeAddonTier: (addonId, tierId) =>
    api.post("/billing/addons/upgrade", { addonId, tierId }),
  cancelAddon: (addonType, immediately) =>
    api.post("/billing/addons/cancel", { addonType, immediately }),
};

// ============================================================================
// USAGE API
// ============================================================================
export const usageApi = {
  getCurrent: () => api.get("/usage"),
  getUsage: () => api.get("/usage"),
  getHistory: (params) => {
    if (typeof params === "number") {
      return api.get("/usage/history", { params: { months: params } });
    }
    return api.get("/usage/history", { params });
  },
  getLimits: (resource) => api.get(`/usage/limits/${resource}`),
  getByFeature: (feature) => api.get(`/usage/feature/${feature}`),
  getRecentActivity: (limit = 10) =>
    api.get("/usage/activity", { params: { limit } }),
  getAllActivity: (page = 1, limit = 20) =>
    api.get("/usage/activity/all", { params: { page, limit } }),
  getEnvelopeStats: () => api.get("/usage/envelope-stats"),
};

// ============================================================================
// SECURITY API (user-facing security settings)
// ============================================================================
export const securityApi = {
  getSessions: () => api.get("/security/sessions"),
  revokeSession: (sessionId) => api.delete(`/security/sessions/${sessionId}`),
  revokeAllSessions: () => api.delete("/security/sessions"),
  getAuditLog: (params) => api.get("/security/audit", { params }),
  getSecuritySettings: () => api.get("/security/settings"),
  updateSecuritySettings: (data) => api.patch("/security/settings", data),
  getLoginHistory: () => api.get("/security/login-history"),
  enableIpRestriction: (ips) => api.post("/security/ip-restriction", { ips }),
  disableIpRestriction: () => api.delete("/security/ip-restriction"),
};

// ============================================================================
// FOLDER API (singular - simpler version)
// ============================================================================
export const folderApi = {
  list: () => api.get("/folders"),
  getAll: () => api.get("/folders"),
  create: (data) => api.post("/folders", data),
  update: (id, data) => api.patch(`/folders/${id}`, data),
  delete: (id) => api.delete(`/folders/${id}`),
  moveEnvelope: (envelopeId, folderId) =>
    api.post(`/folders/${folderId}/envelopes/${envelopeId}`),
  removeEnvelope: (envelopeId, folderId) =>
    api.delete(`/folders/${folderId}/envelopes/${envelopeId}`),
};

// ============================================================================
// TAG API (singular - simpler version)
// ============================================================================
export const tagApi = {
  list: () => api.get("/tags"),
  getAll: () => api.get("/tags"),
  create: (data) => api.post("/tags", data),
  update: (id, data) => api.patch(`/tags/${id}`, data),
  delete: (id) => api.delete(`/tags/${id}`),
  getEnvelopeTags: (envelopeId) => api.get(`/tags/envelope/${envelopeId}`),
  addToEnvelope: (envelopeId, tagId) =>
    api.post(`/tags/envelope/${envelopeId}/${tagId}`),
  removeFromEnvelope: (envelopeId, tagId) =>
    api.delete(`/tags/envelope/${envelopeId}/${tagId}`),
  setEnvelopeTags: (envelopeId, tagIds) =>
    api.put(`/tags/envelope/${envelopeId}`, { tagIds }),
};

// ============================================================================
// MERGE KEY API
// ============================================================================
export const mergeKeyApi = {
  getSettings: () => api.get("/merge-keys/settings"),
  updateSettings: (data) => api.patch("/merge-keys/settings", data),
  list: (page = 1, limit = 12, search = "") =>
    api.get("/merge-keys/definitions", { params: { page, limit, search } }),
  getAll: (page = 1, limit = 12, search = "") =>
    api.get("/merge-keys/definitions", { params: { page, limit, search } }),
  create: (data) => api.post("/merge-keys/definitions", data),
  getById: (id) => api.get(`/merge-keys/definitions/${id}`),
  update: (id, data) => api.patch(`/merge-keys/definitions/${id}`, data),
  delete: (id) => api.delete(`/merge-keys/definitions/${id}`),
  resetToDefaults: () => api.post("/merge-keys/definitions/reset"),
  testDetection: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/merge-keys/test", formData);
  },
  bulkCreate: (keys) => api.post("/merge-keys/definitions/bulk", { keys }),
  getCategories: () => api.get("/merge-keys/categories"),
  createCategory: (data) => api.post("/merge-keys/categories", data),
  updateCategory: (id, data) => api.patch(`/merge-keys/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/merge-keys/categories/${id}`),
  preview: (envelopeId, documentId, recipientId, mergeKeys) =>
    api.post(`/merge-keys/preview`, {
      envelopeId,
      documentId,
      recipientId,
      mergeKeys,
    }),
  getRecipientValues: (envelopeId) =>
    api.get(`/merge-keys/envelope/${envelopeId}/values`),
  setRecipientValues: (envelopeId, recipientId, values) =>
    api.post(
      `/merge-keys/envelope/${envelopeId}/recipient/${recipientId}/values`,
      { values },
    ),
  detectInDocument: (documentId) =>
    api.post(`/merge-keys/detect/${documentId}`),
  getMergeKeysForEnvelope: (envelopeId) =>
    api.get(`/merge-keys/envelope/${envelopeId}`),
};

// ============================================================================
// SIGNER VERIFICATION API (authenticated - for envelope owners)
// ============================================================================
export const signerVerificationApi = {
  checkRequired: (token) =>
    publicApi.get(`/sign/${token}/verification/check`),
  sendCode: (token, method) =>
    publicApi.post(`/sign/${token}/verification/send-code`, { method }),
  verify: (token, value) =>
    publicApi.post(`/sign/${token}/verification/verify`, { value }),
  getStatus: (token) => publicApi.get(`/sign/${token}/verification/status`),
};

// ============================================================================
// RECIPIENT VERIFICATION API (public - for signers)
// ============================================================================
export const recipientVerificationApi = {
  requestCode: (token, method) =>
    publicApi.post(`/sign/${token}/verification/request`, { method }),
  verifyCode: (token, code) =>
    publicApi.post(`/sign/${token}/verification/verify`, { code }),
  getStatus: (token) => publicApi.get(`/sign/${token}/verification/status`),
  getOptions: (token) =>
    publicApi.get(`/sign/${token}/verification/options`),
};

// ============================================================================
// SMS API (authenticated - for organisation users)
// ============================================================================
export const smsApi = {
  getStatus: () => api.get("/sms/status"),
  calculateSegments: (message) =>
    api.post("/sms/calculate-segments", { message }),
  validatePhone: (phone) => api.post("/sms/validate-phone", { phone }),
  sendMessage: (data) => api.post("/sms/send", data),
  sendReminder: (data) => api.post("/sms/reminder", data),
  sendSigningLink: (data) => api.post("/sms/signing-link", data),
  sendVerificationCode: (data) => api.post("/sms/verification/send", data),
  verifyCode: (data) => api.post("/sms/verification/verify", data),
  getTemplates: () => api.get("/sms/templates"),
  getTemplate: (key) => api.get(`/sms/templates/${key}`),
  upsertTemplate: (data) => api.post("/sms/templates", data),
  previewTemplate: (key, variables) =>
    api.post(`/sms/templates/${key}/preview`, { variables }),
  getSettings: () => api.get("/sms/settings"),
  updateSettings: (data) => api.put("/sms/settings", data),
  getMessages: (params) => api.get("/sms/messages", { params }),
  getMessage: (id) => api.get(`/sms/messages/${id}`),
  getAnalytics: (params) => api.get("/sms/analytics", { params }),
  getStats: () => api.get("/sms/stats"),
  optOut: (phone) => api.post("/sms/opt-out", { phone }),
  optIn: (phone) => api.post("/sms/opt-in", { phone }),
  checkOptOut: (phone) => api.get("/sms/opt-out/check", { params: { phone } }),
};

// ============================================================================
// CERTIFICATES API (Digital Signatures)
// ============================================================================
export const certificatesApi = {
  // Settings
  getSettings: () => api.get("/certificates/settings"),
  updateSettings: (data) => api.patch("/certificates/settings", data),

  // Certificate management
  list: () => api.get("/certificates"),
  getById: (id) => api.get(`/certificates/${id}`),
  generate: (data) => api.post("/certificates/generate", data),
  upload: (data) => api.post("/certificates/upload", data),
  uploadP12: (formData) =>
    api.post("/certificates/upload-p12", formData, {
      headers: { "Content-Type": undefined },
    }),
  setDefault: (id) => api.patch(`/certificates/${id}/default`),
  revoke: (id, reason) => api.patch(`/certificates/${id}/revoke`, { reason }),
  delete: (id) => api.delete(`/certificates/${id}`),
  download: (id) =>
    api.get(`/certificates/${id}/download`, { responseType: "blob" }),

  // Expiring certificates
  getExpiring: (days = 30) => api.get(`/certificates/expiring?days=${days}`),

  // Signature details
  getSignatureDetails: (documentId) =>
    api.get(`/certificates/signature/${documentId}`),

  // Verification
  verify: (formData) =>
    api.post("/certificates/verify", formData, {
      headers: { "Content-Type": undefined },
    }),
};

// ============================================================================
// DATA RETENTION API (org admins; /compliance/retention/*)
// ============================================================================
export const retentionApi = {
  getPolicies: () => api.get("/compliance/retention/policies"),
  upsertPolicy: (data) => api.put("/compliance/retention/policies", data),
  deletePolicy: (policyId) =>
    api.delete(`/compliance/retention/policies/${policyId}`),
  initializeDefaults: () =>
    api.post("/compliance/retention/policies/initialize"),
  getSchedule: (params) =>
    api.get("/compliance/retention/schedule", { params }),
  processDueItems: () => api.post("/compliance/retention/schedule/process"),
  getDeletionCertificates: (params) =>
    api.get("/compliance/retention/certificates", { params }),
};

// ============================================================================
// KNOWLEDGE BASE API
// ============================================================================
export const knowledgeBaseApi = {
  // Public endpoints (all accept optional locale parameter)
  getCategories: (locale) => api.get("/kb/categories", { params: { locale } }),
  getArticles: (params) => api.get("/kb/articles", { params }),
  getFeaturedArticles: (locale) =>
    api.get("/kb/articles/featured", { params: { locale } }),
  searchArticles: (q, locale) =>
    api.get("/kb/articles/search", { params: { q, locale } }),
  getArticle: (slug, locale) =>
    api.get(`/kb/articles/${slug}`, { params: { locale } }),
  submitFeedback: (id, helpful) =>
    api.post(`/kb/articles/${id}/feedback`, { helpful }),
  getTags: () => api.get("/kb/tags"),

  // Admin endpoints
  admin: {
    // Categories
    getCategories: () => api.get("/kb/admin/categories"),
    createCategory: (data) => api.post("/kb/admin/categories", data),
    getCategory: (id) => api.get(`/kb/admin/categories/${id}`),
    updateCategory: (id, data) => api.put(`/kb/admin/categories/${id}`, data),
    deleteCategory: (id) => api.delete(`/kb/admin/categories/${id}`),
    reorderCategories: (order) =>
      api.put("/kb/admin/categories/reorder", { order }),

    // Articles
    getArticles: (params) => api.get("/kb/admin/articles", { params }),
    createArticle: (data) => api.post("/kb/admin/articles", data),
    getArticle: (id) => api.get(`/kb/admin/articles/${id}`),
    updateArticle: (id, data) => api.put(`/kb/admin/articles/${id}`, data),
    deleteArticle: (id) => api.delete(`/kb/admin/articles/${id}`),
    publishArticle: (id) => api.put(`/kb/admin/articles/${id}/publish`),
    unpublishArticle: (id) => api.put(`/kb/admin/articles/${id}/unpublish`),
    reorderArticles: (order) =>
      api.put("/kb/admin/articles/reorder", { order }),

    // Tags
    getTags: () => api.get("/kb/admin/tags"),
    createTag: (data) => api.post("/kb/admin/tags", data),
    updateTag: (id, data) => api.put(`/kb/admin/tags/${id}`, data),
    deleteTag: (id) => api.delete(`/kb/admin/tags/${id}`),
  },
};

export default api;
